import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Play, 
  CheckCircle, 
  Clock,
  ArrowLeft,
  Bell,
  X,
  WifiOff,
  RefreshCw,
  Cloud
} from 'lucide-react';
import { useOffline } from '@/contexts/OfflineContext';
import { getOfflineCourses, getOfflineProgress } from '@/services/syncService';
import { saveBulkToStore } from '@/services/offlineDB';
import { useTranslation } from 'react-i18next';

const WorkerTraining = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isOfflineData, setIsOfflineData] = useState(false);
  
  // Offline context
  const { isOnline, pendingCount, manualSync, isSyncing } = useOffline();

  const loadOfflineTrainingData = useCallback(async () => {
    try {
      const [offlineCourses, offlineProgress] = await Promise.all([
        getOfflineCourses(),
        getOfflineProgress()
      ]);
      
      if (offlineCourses && offlineCourses.length > 0) {
        setCourses(offlineCourses);
        setProgress(offlineProgress || []);
        setIsOfflineData(true);
        toast.info('📴 Showing cached training data (offline mode)', { duration: 3000 });
      } else {
        setCourses([]);
        setProgress([]);
        toast.warning(t('training.noCachedTraining'), { duration: 4000 });
      }
    } catch (error) {
      console.error('Failed to load offline training data:', error);
      setCourses([]);
      setProgress([]);
    }
  }, [t]);

  const loadTrainingData = useCallback(async () => {
    setLoading(true);
    
    // Try online first, fall back to offline
    if (isOnline) {
      try {
        const [coursesRes, progressRes] = await Promise.all([
          axiosInstance.get('/courses'),
          axiosInstance.get('/progress')
        ]);
        setCourses(coursesRes.data);
        setProgress(progressRes.data);
        setIsOfflineData(false);
        
        // Cache for offline use
        if (coursesRes.data && coursesRes.data.length > 0) {
          await saveBulkToStore('courses', coursesRes.data);
          console.log('📦 Cached', coursesRes.data.length, 'courses for offline');
        }
        if (progressRes.data && progressRes.data.length > 0) {
          await saveBulkToStore('progress', progressRes.data);
          console.log('📦 Cached training progress for offline');
        }
      } catch (error) {
        console.error('Failed to load training data online, trying offline:', error);
        await loadOfflineTrainingData();
      }
    } else {
      await loadOfflineTrainingData();
    }
    
    setLoading(false);
  }, [isOnline, loadOfflineTrainingData]);

  useEffect(() => {
    loadTrainingData();
    
    // Check if we came from a notification
    if (location.state?.notificationMessage) {
      setNotificationMessage(location.state.notificationMessage);
      setShowNotificationBanner(true);
      
      // Clear the location state
      window.history.replaceState({}, document.title);
    }
  }, [location, loadTrainingData]);

  const getCourseProgress = (courseId) => {
    return progress.find(p => p.courseId === courseId);
  };

  const startCourse = async (courseId) => {
    try {
      await axiosInstance.post(`/progress/start?course_id=${courseId}`);
      navigate(`/course/${courseId}`);
    } catch (error) {
      console.error('Failed to start course:', error);
      toast.error(t('training.failedStartCourse'));
    }
  };

  const completedCourses = progress.filter(p => p.completed).length;
  const inProgressCourses = progress.filter(p => !p.completed && p.progress > 0).length;
  const totalProgress = courses.length > 0 ? (completedCourses / courses.length) * 100 : 0;

  const availableCourses = courses.filter(course => !getCourseProgress(course.id));
  const myCourses = courses.filter(course => {
    const prog = getCourseProgress(course.id);
    return prog && !prog.completed;
  });
  const completedCoursesList = courses.filter(course => {
    const prog = getCourseProgress(course.id);
    return prog && prog.completed;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('training.loadingTraining')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Offline Mode Banner */}
        {!isOnline && (
          <div className="mb-4 p-3 bg-orange-100 border border-orange-300 rounded-lg flex items-center gap-3">
            <WifiOff className="h-5 w-5 text-orange-700 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-900">{t('common.offlineMode')}</p>
              <p className="text-xs text-orange-700">{t('training.offlineTrainingNote')}</p>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                {t('training.title')}
                {isOfflineData && (
                  <span className="inline-flex items-center gap-1 text-sm font-normal text-orange-600 bg-orange-50 px-2 py-1 rounded">
                    <WifiOff className="w-4 h-4" />
                    {t('common.offlineMode')}
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-1">{t('training.manageCoursesDesc')}</p>
            </div>
          </div>
          {pendingCount > 0 && isOnline && (
            <Button
              onClick={manualSync}
              variant="outline"
              disabled={isSyncing}
              className="text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              Sync ({pendingCount})
            </Button>
          )}
        </div>

        {/* Notification Banner */}
        {showNotificationBanner && (
          <Alert className="mb-6 bg-orange-50 border-orange-200">
            <Bell className="h-4 w-4 text-orange-600" />
            <AlertTitle className="text-orange-900">{t('training.trainingReminder')}</AlertTitle>
            <AlertDescription className="text-orange-800 flex items-start justify-between">
              <span>{notificationMessage}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotificationBanner(false)}
                className="ml-2 h-6 w-6 p-0 hover:bg-orange-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Courses</p>
                  <p className="text-2xl font-bold text-gray-900">{courses.length}</p>
                </div>
                <BookOpen className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{completedCourses}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">In Progress</p>
                  <p className="text-2xl font-bold text-orange-600">{inProgressCourses}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600 mb-2">Overall Progress</p>
                <Progress value={totalProgress} className="mb-2" />
                <p className="text-sm font-semibold text-gray-900">{Math.round(totalProgress)}%</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Tabs */}
        <Tabs defaultValue="available" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="available">Available Courses</TabsTrigger>
            <TabsTrigger value="my-courses">My Courses</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          <TabsContent value="available" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableCourses.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No available courses</p>
                </div>
              ) : (
                availableCourses.map((course) => (
                  <Card key={course.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                    <div className="h-40 bg-gradient-to-br from-orange-500 to-gray-900 flex items-center justify-center p-6">
                      <h3 className="text-2xl font-bold text-white text-center leading-tight">
                        {course.title}
                      </h3>
                    </div>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <Badge className="bg-orange-100 text-orange-800">{course.category}</Badge>
                        <span className="text-sm text-gray-600 flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {course.duration} min
                        </span>
                      </div>
                      <Button 
                        onClick={() => startCourse(course.id)}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Course
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="my-courses" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myCourses.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No courses in progress</p>
                </div>
              ) : (
                myCourses.map((course) => {
                  const prog = getCourseProgress(course.id);
                  return (
                    <Card key={course.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                      <div className="h-40 bg-gradient-to-br from-orange-500 to-gray-900 flex items-center justify-center p-6">
                        <h3 className="text-2xl font-bold text-white text-center leading-tight">
                          {course.title}
                        </h3>
                      </div>
                      <CardContent className="pt-4">
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Progress</span>
                            <span className="font-semibold">{prog?.progress || 0}%</span>
                          </div>
                          <Progress value={prog?.progress || 0} className="h-2" />
                        </div>
                        <Button 
                          onClick={() => navigate(`/course/${course.id}`)}
                          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                        >
                          Continue Course
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="completed" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {completedCoursesList.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No completed courses yet</p>
                </div>
              ) : (
                completedCoursesList.map((course) => {
                  const prog = getCourseProgress(course.id);
                  return (
                    <Card key={course.id} className="hover:shadow-lg transition-shadow border-green-200 overflow-hidden">
                      <div className="h-40 bg-gradient-to-br from-orange-500 to-gray-900 flex items-center justify-center p-6 relative">
                        <h3 className="text-2xl font-bold text-white text-center leading-tight">
                          {course.title}
                        </h3>
                        <CheckCircle className="absolute top-4 right-4 w-8 h-8 text-white bg-green-500 rounded-full p-1" />
                      </div>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600 mb-4">
                          Completed: {new Date(prog?.completedAt).toLocaleDateString()}
                        </div>
                        <Button 
                          onClick={() => navigate(`/course/${course.id}`)}
                          variant="outline"
                          className="w-full border-gray-800 text-gray-800 hover:bg-gray-100"
                        >
                          Review Course
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default WorkerTraining;
